/**
 * feat-338 — Question Analysis (Administration → Question Analysis).
 *
 * Which Learn questions people miss, get stuck on, or take too long on, and how lesson
 * time compares by store. Admin + Corporate only (CorporateAdminRoute in App.tsx; the
 * API also 403s the manager role, and a 403 here renders the same AccessDeniedCard).
 *
 * Data: CONTRACT.md r2 via services/questionAnalysisService.ts. The backend computes
 * every metric, flag and suppression; this page formats and orders them. Aggregates
 * only: no learner identifier exists anywhere in what it receives.
 *
 * Design: worktrees/feat-338-question-analysis/design/ (NOTES.md + 01–04 mockups).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Info, Loader2, Search } from 'lucide-react';
import ScorecardNavigation from '../../ScorecardNavigation';
import AccessDeniedCard from '../../../auth/AccessDeniedCard';
import { useTenant } from '../../../../contexts/TenantContext';
import {
  getQuestions,
  getTime,
  QuestionRow,
  QuestionsResponse,
  TimeResponse,
} from '../../../../services/questionAnalysisService';
import {
  C,
  DEFAULT_QUESTION_SORT,
  DEFAULT_SUPPRESS_BELOW_LEARNERS,
  FLAG,
  FLAG_ORDER,
  QA_COPY,
  QuestionSort,
  formatAnswerSeconds,
  nextQuestionSort,
  sortQuestions,
} from './questionAnalysisFormat';
import { ErrorBanner, SuppressFloorContext, httpStatusOf } from './parts';
import FilterBar, { Option } from './FilterBar';
import SummaryStrip from './SummaryStrip';
import QuestionsTable from './QuestionsTable';
import QuestionDetailPanel from './QuestionDetailPanel';
import StoresAndLessons, { STORES_COPY } from './StoresAndLessons';
import { buildQuestionsCsv, csvFilename, downloadCsv } from './questionAnalysisCsv';
import { apiFilters, hasNarrowingFilter, useQaFilters } from './useQaFilters';

interface Load<T> {
  data: T | null;
  loading: boolean;
  error: unknown;
}
const IDLE = { data: null, loading: false, error: null };

/** Fetch `fn` whenever `key` changes; stale responses are dropped. */
function useLoad<T>(key: string | null, fn: () => Promise<T>): Load<T> & { reload: () => void } {
  const [state, setState] = useState<Load<T>>(IDLE);
  const [nonce, setNonce] = useState(0);
  const seq = useRef(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const lastKey = useRef<string | null>(null);
  useEffect(() => {
    if (key === null) {
      setState(IDLE);
      return;
    }
    const keyChangedTenant = lastKey.current?.split('|')[0] !== key.split('|')[0];
    lastKey.current = key;
    const my = ++seq.current;
    setState((s) => ({ data: keyChangedTenant ? null : s.data, loading: true, error: null }));
    fnRef.current().then(
      (data) => {
        if (my === seq.current) setState({ data, loading: false, error: null });
      },
      (error) => {
        if (my === seq.current) setState((s) => ({ data: s.data, loading: false, error: error ?? new Error('failed') }));
      },
    );
  }, [key, nonce]);
  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { ...state, reload };
}

const QuestionAnalysisPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedTenant, tenantName } = useTenant();
  const tenantId = typeof selectedTenant === 'string' && selectedTenant ? selectedTenant : null;
  const { filters, update, reset } = useQaFilters();
  const api = useMemo(() => apiFilters(filters), [filters]);
  const apiKey = tenantId ? `${tenantId}|${JSON.stringify(api)}` : null;

  const questions = useLoad<QuestionsResponse>(apiKey, () => getQuestions(tenantId!, api));
  const time = useLoad<TimeResponse>(apiKey, () => getTime(tenantId!, api));

  const [sort, setSort] = useState<QuestionSort>(DEFAULT_QUESTION_SORT);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Collapse the open row when the data context changes (tenant or filters).
  useEffect(() => setExpandedId(null), [apiKey]);

  // Lesson options: union of every drill seen from /time for this tenant (FRONTEND-QUESTION Q2).
  const [drillOptions, setDrillOptions] = useState<Map<string, string>>(new Map());
  useEffect(() => setDrillOptions(new Map()), [tenantId]);
  useEffect(() => {
    const drills = time.data?.drills;
    if (!drills?.length) return;
    setDrillOptions((cur) => {
      let changed = false;
      const next = new Map(cur);
      drills.forEach((d) => {
        if (!next.has(d.drill_id)) {
          next.set(d.drill_id, d.drill_name);
          changed = true;
        }
      });
      return changed ? next : cur;
    });
  }, [time.data]);
  const drillList: Option[] = useMemo(
    () => Array.from(drillOptions, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)),
    [drillOptions],
  );
  // Store options: /time.locations always lists every location (it ignores the Store filter).
  const locationList: Option[] = useMemo(
    () =>
      (time.data?.locations ?? [])
        .map((l) => ({ id: l.location_id, name: l.location_name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [time.data],
  );

  const qData = questions.data;

  // The learner floor: from the most recent /questions meta, kept across refetches and
  // errors; the exported default until one has loaded (FIX-SLICE item 2).
  // Derived during render (not in an effect) so no frame ever shows a stale floor.
  const floorRef = useRef<number>(DEFAULT_SUPPRESS_BELOW_LEARNERS);
  const loadedFloor = qData?.meta.thresholds.suppress_below_learners;
  if (typeof loadedFloor === 'number') floorRef.current = loadedFloor;
  const floor = floorRef.current;
  const visibleRows: QuestionRow[] = useMemo(() => {
    if (!qData) return [];
    const rows = filters.flagged_only ? qData.questions.filter((r) => r.flags.length > 0) : qData.questions;
    return sortQuestions(rows, sort);
  }, [qData, filters.flagged_only, sort]);

  if (httpStatusOf(questions.error) === 403 || httpStatusOf(time.error) === 403) {
    return <AccessDeniedCard message={QA_COPY.deniedMessage} />;
  }

  const firstLoad = !qData && questions.loading;
  const refetching = !!qData && questions.loading;
  const isEmpty = !!qData && qData.questions.length === 0 && !hasNarrowingFilter(filters);
  const datesSet = !!(filters.start || filters.end);
  const flaggedCount = qData ? qData.questions.filter((r) => r.flags.length > 0).length : 0;

  const renderDetail = (row: QuestionRow) =>
    qData && tenantId ? (
      <QuestionDetailPanel tenantId={tenantId} questionId={row.question_id} filters={api} meta={qData.meta} />
    ) : null;

  return (
    <SuppressFloorContext.Provider value={floor}>
      <div className="min-h-screen bg-cream">
      <ScorecardNavigation />
      <main className="max-w-[1600px] mx-auto p-4 md:p-8">
        <button
          type="button"
          onClick={() => navigate('/restaurant/administration')}
          className="inline-flex items-center gap-1.5 text-sm text-inkMuted hover:text-ink mb-4"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          {QA_COPY.back}
        </button>

        <div className="mb-5">
          <h1 className="text-3xl font-display font-semibold text-ink mb-1">{QA_COPY.title}</h1>
          <p className="text-sm text-inkMuted max-w-3xl">{QA_COPY.intro(tenantName || '', floor)}</p>
          <p className="text-xs text-inkMuted mt-1.5 inline-flex items-center gap-1">
            <Info className="w-3.5 h-3.5" aria-hidden="true" />
            {QA_COPY.dataStart}
          </p>
        </div>

        {!tenantId && (
          <div className="mb-5 rounded-2xl border border-softBorder bg-white p-4 text-sm text-ink" data-testid="no-tenant">
            {QA_COPY.noTenant}
          </div>
        )}

        {tenantId && (
          <>
            <FilterBar filters={filters} locations={locationList} drills={drillList} onChange={update} onReset={reset} />

            {!!questions.error && (
              <ErrorBanner
                testId="questions-error"
                className="mb-5"
                title={QA_COPY.questionsFailed}
                detail={QA_COPY.failedDetail(httpStatusOf(questions.error))}
                onRetry={questions.reload}
                busy={questions.loading}
              />
            )}

            {(firstLoad || refetching) && (
              <div className="flex items-center gap-2 text-sm text-inkMuted mb-4" role="status" data-testid="loading-line">
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                {QA_COPY.loading}
              </div>
            )}

            {firstLoad && (
              <div data-testid="first-load">
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 mb-5" aria-busy="true">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="bg-white rounded-2xl border border-softBorder p-4">
                      <div className="h-3 w-20 mb-3 rounded bg-cream-dark animate-pulse" />
                      <div className="h-7 w-16 rounded bg-cream-dark animate-pulse" />
                    </div>
                  ))}
                </div>
                <section className={C.section} aria-busy="true">
                  <div className="h-5 w-32 mb-2 rounded bg-cream-dark animate-pulse" />
                  <div className="h-4 w-64 mb-4 rounded bg-cream-dark animate-pulse" />
                  <div className="space-y-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="h-12 rounded-lg bg-cream animate-pulse" />
                    ))}
                  </div>
                </section>
              </div>
            )}

            {qData && isEmpty && (
              <div data-testid="empty-state" className={`${C.section} text-center py-10`}>
                <div className="mx-auto w-12 h-12 rounded-full bg-cream-dark flex items-center justify-center mb-3" aria-hidden="true">
                  <Search className="w-6 h-6 text-inkMuted" />
                </div>
                <p className="font-display text-lg font-semibold text-ink">{QA_COPY.emptyTitle}</p>
                <p className="text-sm text-inkMuted mt-1">{QA_COPY.emptyDetail}</p>
                {datesSet && (
                  <button
                    type="button"
                    data-testid="show-all-dates"
                    className={`${C.btnSecondary} mt-4`}
                    onClick={() => update({ start: undefined, end: undefined })}
                  >
                    {QA_COPY.showAllDates}
                  </button>
                )}
              </div>
            )}

            {qData && !isEmpty && (
              <div className={refetching ? 'opacity-60' : ''} data-testid="questions-content">
                <SummaryStrip summary={qData.summary} />

                <section className={C.section} aria-labelledby="qa-questions">
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <h2 id="qa-questions" className={C.h2}>
                        {QA_COPY.questions}
                      </h2>
                      {visibleRows.length ? (
                        <p className={C.sub} data-testid="questions-sub">
                          {QA_COPY.questionsSub(visibleRows.length, flaggedCount).count} ·{' '}
                          <b className="text-ink">{QA_COPY.questionsSub(visibleRows.length, flaggedCount).flagged}</b> ·{' '}
                          {QA_COPY.questionsOrder}
                        </p>
                      ) : (
                        <p className={C.sub}>{QA_COPY.noMatchSub}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      data-testid="export-csv"
                      className={C.btnSecondary}
                      disabled={!visibleRows.length}
                      title={visibleRows.length ? undefined : QA_COPY.nothingToExport}
                      onClick={() => downloadCsv(csvFilename(tenantName || '', api), buildQuestionsCsv(visibleRows, floor))}
                    >
                      <Download className="w-4 h-4" aria-hidden="true" />
                      {QA_COPY.exportCsv}
                    </button>
                  </div>

                  <VersionNote meta={qData.meta} />
                  <FlagKey data={qData} />

                  {visibleRows.length ? (
                    <>
                      <QuestionsTable
                        rows={visibleRows}
                        meta={qData.meta}
                        sort={sort}
                        onSort={(k) => setSort((cur) => nextQuestionSort(cur, k))}
                        expandedId={expandedId}
                        onToggle={(id) => setExpandedId((cur) => (cur === id ? null : id))}
                        renderDetail={renderDetail}
                      />
                      <p className="text-xs text-inkMuted mt-2">{QA_COPY.tableFoot}</p>
                    </>
                  ) : (
                    <div className="rounded-2xl border border-softBorder bg-white p-4 text-sm" data-testid="no-match">
                      <p className="text-ink font-semibold">{QA_COPY.noMatchTitle}</p>
                      {filters.flagged_only && (
                        <p className="text-xs text-inkMuted mt-1">
                          {QA_COPY.noMatchFlagged.a}
                          <b className="text-ink">{QA_COPY.noMatchFlagged.b}</b>
                          {QA_COPY.noMatchFlagged.c}
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={reset}
                        className="mt-3 text-sm font-bold text-ink underline underline-offset-2 hover:text-coral"
                      >
                        {QA_COPY.resetFilters}
                      </button>
                    </div>
                  )}
                </section>
              </div>
            )}

            {/* Stores + Lessons render independently of the Questions empty state (DESIGN-QUESTION Q6). */}
            {!!time.error && (
              <section className={C.section} data-testid="time-error-section">
                <h2 className={C.h2}>{STORES_COPY.storesTitle}</h2>
                <ErrorBanner
                  testId="time-error"
                  className="mt-3"
                  title={QA_COPY.timeFailed}
                  detail={QA_COPY.timeFailedDetail}
                  onRetry={time.reload}
                  busy={time.loading}
                />
              </section>
            )}
            {!time.data && time.loading && (
              <section className={C.section} aria-busy="true" data-testid="time-loading">
                <div className="flex items-center gap-2 text-sm text-inkMuted mb-3" role="status">
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                  {STORES_COPY.loading}
                </div>
                <div className="space-y-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="h-10 rounded-lg bg-cream animate-pulse" />
                  ))}
                </div>
              </section>
            )}
            {time.data && !time.error && <StoresAndLessons data={time.data} dim={time.loading} />}
          </>
        )}
      </main>
      </div>
    </SuppressFloorContext.Provider>
  );
};

const VersionNote: React.FC<{ meta: QuestionsResponse['meta'] }> = ({ meta }) => {
  if (meta.include_earlier_versions) {
    return (
      <p className="text-xs text-inkMuted mb-3" data-testid="version-note">
        {QA_COPY.versionAll}
      </p>
    );
  }
  if (!meta.excluded_unknown_version) {
    return (
      <p className="text-xs text-inkMuted mb-3" data-testid="version-note">
        {QA_COPY.versionCurrentNoneLeft}
      </p>
    );
  }
  const t = QA_COPY.versionCurrent(meta.excluded_unknown_version);
  return (
    <p className="text-xs text-inkMuted mb-3" data-testid="version-note">
      {t.a}
      <span className="text-ink font-semibold">{t.b}</span>
      {t.c}
      <b className="text-ink">{t.d}</b>
      {t.e}
    </p>
  );
};

const FlagKey: React.FC<{ data: QuestionsResponse }> = ({ data }) => {
  const t = data.meta.thresholds;
  const m = data.meta.type_median_answer_ms;
  return (
    <>
      <div className="text-xs text-inkMuted flex flex-wrap items-center gap-x-4 gap-y-1 mb-3" data-testid="flag-key">
        <span className="font-bold text-ink">{QA_COPY.flagKeyLead(t.min_learners_to_flag)}</span>
        {FLAG_ORDER.map((k) => {
          const f = FLAG[k];
          return (
            <span key={k} className="inline-flex items-center gap-1.5">
              <span className={C.flagChip}>
                <f.Icon className="w-3 h-3" aria-hidden="true" />
                {f.label}
              </span>
              {f.rule(t)}
            </span>
          );
        })}
      </div>
      <p className="text-xs text-inkMuted mb-3" data-testid="usual-time">
        {QA_COPY.usualTime(formatAnswerSeconds(m.multiple_choice), formatAnswerSeconds(m.fill_blank))}
      </p>
    </>
  );
};

export default QuestionAnalysisPage;
